// src/pages/SuperAdminOrgDetail.jsx

import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Building2, ArrowLeft, Users, Mail, CheckCircle2, XCircle, Clock,
  Loader2, AlertCircle, ShieldOff, ShieldCheck, Pencil, X,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card.jsx';
import { Badge } from '@/components/ui/badge.jsx';
import { Button } from '@/components/ui/button.jsx';
import { Input, Label } from '@/components/ui/input.jsx';
import {
  TableWrapper, Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/table.jsx';
import { apiCall } from '../utils/api';
import BIAccessSection from '../components/BIAccessSection';

const STATUS_STYLES = {
  active:    'bg-green-500/20 text-green-400 border-green-500/30',
  suspended: 'bg-red-500/20 text-red-400 border-red-500/30',
  cancelled: 'bg-neutral-500/20 text-neutral-400 border-neutral-500/30',
  pending:   'bg-amber-500/20 text-amber-400 border-amber-500/30',
  accepted:  'bg-green-500/20 text-green-400 border-green-500/30',
  revoked:   'bg-neutral-500/20 text-neutral-400 border-neutral-500/30',
  expired:   'bg-orange-500/20 text-orange-400 border-orange-500/30',
};

function formatLimit(max) {
  return max === null || max === undefined ? 'Unlimited' : max;
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

export default function SuperAdminOrgDetail() {
  const { orgId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyAction, setBusyAction] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const load = async () => {
    setError('');
    try {
      const res = await apiCall(`/super-admin/organizations/${orgId}`);
      if (res.ok) {
        setData(await res.json());
      } else if (res.status === 404) {
        setError('Organization not found.');
      } else if (res.status === 401) {
        setError('Session expired. Verifying again...');
      } else {
        setError('Could not load organization.');
      }
    } catch (err) {
      console.error(err);
      setError('Network error.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [orgId]);

  const changeStatus = async (newStatus) => {
    if (!data?.organization) return;
    const orgName = data.organization.name;
    const verb = newStatus === 'active' ? 'reactivate' : newStatus;
    const warning = newStatus === 'suspended'
      ? `Suspend "${orgName}"? All users in this org will be unable to log in. Existing tokens will be rejected immediately.`
      : newStatus === 'cancelled'
      ? `Cancel "${orgName}"? This blocks all access (same effect as suspend, but signals permanent shutdown).`
      : `Reactivate "${orgName}"? Users will be able to log in again.`;
    if (!window.confirm(warning)) return;

    setBusyAction(verb);
    try {
      const res = await apiCall(
        `/super-admin/organizations/${data.organization.id}/status`,
        { method: 'POST', body: JSON.stringify({ status: newStatus }) }
      );
      if (res.ok) {
        await load();
      } else {
        const errBody = await res.json().catch(() => ({}));
        alert(errBody.detail || `Could not ${verb} the organization.`);
      }
    } catch (err) {
      console.error(err);
      alert('Network error.');
    } finally {
      setBusyAction(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-[#a0a0a0]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <Link
          to="/super-admin/organizations"
          className="inline-flex items-center gap-1 text-sm text-[#a0a0a0] hover:text-white"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to organizations
        </Link>
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-2">
          <AlertCircle className="h-4 w-4 text-red-400 mt-0.5" />
          <p className="text-sm text-red-400">{error}</p>
        </div>
      </div>
    );
  }

  const { organization: org, users, recent_invitations: invitations } = data;

  return (
    <div className="space-y-8">
      {/* Back link */}
      <Link
        to="/super-admin/organizations"
        className="inline-flex items-center gap-1 text-sm text-[#a0a0a0] hover:text-white"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to organizations
      </Link>

      {/* Header */}
      <div className="flex items-start gap-4">
        <div className="h-14 w-14 rounded-xl bg-[#2a2a2a] flex items-center justify-center flex-shrink-0">
          <Building2 className="h-7 w-7 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-semibold text-white">{org.name}</h2>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Badge variant="secondary">Plan: {org.plan}</Badge>
            <Badge
              variant="default"
              className={STATUS_STYLES[org.status] || STATUS_STYLES.active}
            >
              {org.status}
            </Badge>
            <span className="text-xs text-[#a0a0a0] font-mono ml-2">{org.id}</span>
          </div>
          {org.created_at && (
            <p className="text-xs text-[#a0a0a0] mt-2">
              Created {formatDate(org.created_at)}
            </p>
          )}
        </div>
        {/* Action buttons */}
        <div className="flex flex-col gap-2 flex-shrink-0">
          <Button
            variant="ghost"
            size="sm"
            disabled={busyAction !== null}
            onClick={() => setShowEditModal(true)}
          >
            <Pencil className="h-4 w-4 mr-2" />
            Edit
          </Button>
          {org.status === 'active' ? (
            <Button
              variant="ghost"
              size="sm"
              disabled={busyAction !== null}
              onClick={() => changeStatus('suspended')}
              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
            >
              <ShieldOff className="h-4 w-4 mr-2" />
              {busyAction === 'suspend' ? 'Suspending...' : 'Suspend'}
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              disabled={busyAction !== null}
              onClick={() => changeStatus('active')}
              className="text-green-400 hover:text-green-300 hover:bg-green-500/10"
            >
              <ShieldCheck className="h-4 w-4 mr-2" />
              {busyAction === 'reactivate' ? 'Reactivating...' : 'Reactivate'}
            </Button>
          )}
        </div>
      </div>

      {/* Plan details */}
      <div>
        <h3 className="text-sm font-semibold uppercase tracking-wide text-[#a0a0a0] mb-3">
          Plan details
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Card>
            <CardContent className="p-4">
              <div className="text-xs text-[#a0a0a0] mb-1">User limit</div>
              <div className="text-xl font-semibold text-white">{formatLimit(org.max_users)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-xs text-[#a0a0a0] mb-1">Chart limit</div>
              <div className="text-xl font-semibold text-white">{formatLimit(org.max_charts)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="text-xs text-[#a0a0a0] mb-1">Connector limit</div>
              <div className="text-xl font-semibold text-white">{formatLimit(org.max_connectors)}</div>
            </CardContent>
          </Card>
        </div>
        {org.trial_ends_at && (
          <p className="text-xs text-[#a0a0a0] mt-2">
            Trial ends {formatDate(org.trial_ends_at)}
          </p>
        )}
      </div>

      {/* Users */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Users className="h-4 w-4 text-[#a0a0a0]" />
          <h3 className="text-sm font-semibold uppercase tracking-wide text-[#a0a0a0]">
            Users ({users.length})
          </h3>
        </div>
        <Card>
          <CardContent className="p-0">
            <TableWrapper>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Roles</TableHead>
                    <TableHead>Active</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map(u => (
                    <TableRow key={u.id}>
                      <TableCell className="font-medium text-white">{u.email}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {u.roles.length === 0 && <span className="text-[#a0a0a0] text-xs">—</span>}
                          {u.roles.map(r => (
                            <Badge
                              key={r}
                              variant="secondary"
                              className={r === 'super_admin' ? 'bg-amber-500/20 text-amber-300 border-amber-500/30' : ''}
                            >
                              {r}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        {u.is_active ? (
                          <span className="inline-flex items-center gap-1 text-green-400 text-xs">
                            <CheckCircle2 className="h-3.5 w-3.5" /> Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-neutral-400 text-xs">
                            <XCircle className="h-3.5 w-3.5" /> Deactivated
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-[#a0a0a0] text-sm">{formatDate(u.created_at)}</TableCell>
                    </TableRow>
                  ))}
                  {users.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-[#a0a0a0] py-6">
                        No users yet.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableWrapper>
          </CardContent>
        </Card>
      </div>

      {/* Recent invitations */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Mail className="h-4 w-4 text-[#a0a0a0]" />
          <h3 className="text-sm font-semibold uppercase tracking-wide text-[#a0a0a0]">
            Recent invitations ({invitations.length})
          </h3>
        </div>
        <Card>
          <CardContent className="p-0">
            <TableWrapper>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Sent</TableHead>
                    <TableHead>Expires</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invitations.map(inv => (
                    <TableRow key={inv.id}>
                      <TableCell className="text-[#e0e0e0]">{inv.email}</TableCell>
                      <TableCell><Badge variant="secondary">{inv.role}</Badge></TableCell>
                      <TableCell>
                        <Badge variant="default" className={STATUS_STYLES[inv.status] || ''}>
                          {inv.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-[#a0a0a0] text-sm">{formatDate(inv.created_at)}</TableCell>
                      <TableCell className="text-[#a0a0a0] text-sm">
                        <span className="inline-flex items-center gap-1">
                          <Clock className="h-3.5 w-3.5" />
                          {formatDate(inv.expires_at)}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                  {invitations.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-[#a0a0a0] py-6">
                        No invitations yet.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableWrapper>
          </CardContent>
        </Card>

        {/* BI Access Section */}
        <BIAccessSection
          apiPrefix={`/super-admin/organizations/${orgId}/bi-access`}
        />
      </div>

      {/* Edit modal */}
      {showEditModal && (
        <EditOrgModal
          organization={org}
          onClose={() => setShowEditModal(false)}
          onSaved={async () => { setShowEditModal(false); await load(); }}
        />
      )}
    </div>
  );
}


// ─── Edit Organization Modal ────────────────────────────────────────────

const ASSIGNABLE_PLANS = ['solo', 'crew', 'studio', 'enterprise', 'grandfather', 'custom'];

function limitInputValue(v) {
  if (v === null || v === undefined) return '';
  return String(v);
}

function parseLimitInput(s) {
  const trimmed = (s ?? '').trim();
  if (trimmed === '' || trimmed.toLowerCase() === 'unlimited') return null;
  const n = parseInt(trimmed, 10);
  if (Number.isNaN(n) || n < 0) return undefined;
  return n;
}

function EditOrgModal({ organization: org, onClose, onSaved }) {
  const [name, setName] = useState(org.name || '');
  const [plan, setPlan] = useState(org.plan || 'custom');
  const [applyTierDefaults, setApplyTierDefaults] = useState(false);
  const [maxUsers, setMaxUsers] = useState(limitInputValue(org.max_users));
  const [maxCharts, setMaxCharts] = useState(limitInputValue(org.max_charts));
  const [maxConnectors, setMaxConnectors] = useState(limitInputValue(org.max_connectors));
  const [trialDays, setTrialDays] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const planChanged = plan !== (org.plan || 'custom');

  const submit = async (e) => {
    e.preventDefault();
    setError('');

    const u = parseLimitInput(maxUsers);
    const c = parseLimitInput(maxCharts);
    const k = parseLimitInput(maxConnectors);
    if (u === undefined || c === undefined || k === undefined) {
      setError('Limits must be a non-negative integer or empty (= unlimited).');
      return;
    }

    const payload = {};
    if ((name || '').trim() !== (org.name || '')) {
      payload.name = name.trim();
    }
    if (planChanged) {
      payload.plan = plan;
      if (applyTierDefaults) payload.apply_tier_defaults = true;
    }
    if (u !== org.max_users) payload.max_users = u;
    if (c !== org.max_charts) payload.max_charts = c;
    if (k !== org.max_connectors) payload.max_connectors = k;
    if (trialDays.trim() !== '') {
      const td = parseInt(trialDays, 10);
      if (Number.isNaN(td) || td < 0) {
        setError('Trial days must be a non-negative integer.');
        return;
      }
      payload.trial_days = td;
    }

    if (Object.keys(payload).length === 0) {
      setError('No changes to save.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await apiCall(`/super-admin/organizations/${org.id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        await onSaved();
      } else {
        const errBody = await res.json().catch(() => ({}));
        setError(typeof errBody.detail === 'string' ? errBody.detail : 'Could not save changes.');
      }
    } catch (err) {
      console.error(err);
      setError('Network error.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="w-full max-w-lg bg-[#0f0f0f] border border-[#2a2a2a] rounded-2xl p-6 shadow-2xl">
        <div className="flex items-start justify-between mb-5">
          <div>
            <h3 className="text-lg font-semibold text-white">Edit organization</h3>
            <p className="text-sm text-[#a0a0a0] mt-0.5">{org.name}</p>
          </div>
          <button
            onClick={onClose}
            className="text-[#a0a0a0] hover:text-white"
            disabled={submitting}
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4 max-h-[70vh] overflow-y-auto">
          <div>
            <Label>Name</Label>
            <Input value={name} onChange={e => setName(e.target.value)} disabled={submitting} required />
          </div>

          <div>
            <Label>Plan</Label>
            <select
              value={plan}
              onChange={e => setPlan(e.target.value)}
              disabled={submitting}
              className="w-full bg-[#0a0a0a] border border-[#2a2a2a] text-white text-sm rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-white"
            >
              {ASSIGNABLE_PLANS.map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
            {planChanged && (
              <label className="mt-2 flex items-center gap-2 text-sm text-[#a0a0a0] cursor-pointer">
                <input
                  type="checkbox"
                  checked={applyTierDefaults}
                  onChange={e => setApplyTierDefaults(e.target.checked)}
                  disabled={submitting}
                  className="rounded border-[#2a2a2a]"
                />
                Apply <span className="text-white font-medium">{plan}</span> tier defaults
                <span className="text-xs">(per-field overrides below still win)</span>
              </label>
            )}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Max users</Label>
              <Input value={maxUsers} onChange={e => setMaxUsers(e.target.value)} placeholder="Unlimited" disabled={submitting} />
            </div>
            <div>
              <Label>Max charts</Label>
              <Input value={maxCharts} onChange={e => setMaxCharts(e.target.value)} placeholder="Unlimited" disabled={submitting} />
            </div>
            <div>
              <Label>Max connectors</Label>
              <Input value={maxConnectors} onChange={e => setMaxConnectors(e.target.value)} placeholder="Unlimited" disabled={submitting} />
            </div>
          </div>

          <div>
            <Label>Trial days from now (optional)</Label>
            <Input
              type="number"
              min={0}
              value={trialDays}
              onChange={e => setTrialDays(e.target.value)}
              placeholder="Leave empty to keep current"
              disabled={submitting}
            />
            <p className="text-xs text-[#a0a0a0] mt-1">
              Enter 0 to clear the trial. Leave empty to keep unchanged.
            </p>
          </div>

          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
              <AlertCircle className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-[#2a2a2a]">
            <Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Saving...' : 'Save changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}